<template>
  <div id="app">
    <router-view />
  </div>
</template>

<script>
export default {
  name: 'App',
  async created() {
    const saved = localStorage.getItem('theme')
    if (saved === 'dark') {
      document.documentElement.classList.add('dark')
    }
    
    // Fetch system settings
    await this.$store.dispatch('fetchSettings')
    
    // Update document title
    const title = this.$store.state.settings.browser_title
    if (title) {
      document.title = title
    }
    
    // Update favicon if needed (optional)
    const favicon = this.$store.state.settings.favicon_url
    if (favicon) {
      const link = document.querySelector("link[rel*='icon']") || document.createElement('link')
      link.type = 'image/x-icon'
      link.rel = 'shortcut icon'
      link.href = favicon
      document.getElementsByTagName('head')[0].appendChild(link)
    }
  }
}
</script>

<style>
:root {
  --color-primary: #409eff;
  --color-success: #67c23a;
  --color-warning: #e6a23c;
  --color-danger: #f56c6c;
  --color-info: #909399;

  --bg-body: #f5f7fb;
  --bg-card: #ffffff;
  --text-primary: #1f2d3d;
  --text-regular: #606266;
  --text-secondary: #909399;
  --border-color: #eef2f7;
  --header-bg: #ffffff;
  --aside-bg: #ffffff;
  
  /* Element UI Colors */
  --el-table-bg: #ffffff;
  --el-table-tr-bg: #ffffff;
  --el-table-stripe-bg: #fafafa;
  --el-table-hover-bg: #f5f7fa;
  --el-table-border-color: #ebeef5;
  --el-table-header-color: #909399;
  --el-table-text-color: #606266;
  
  --el-input-bg: #ffffff;
  --el-input-border: #dcdfe6;
  --el-input-text: #606266;
  --el-input-disabled-bg: #f5f7fa;
  
  --el-menu-hover-bg: #ecf5ff;
  --el-menu-active-bg: #ecf5ff;

  --table-header-bg: #f5f7fa;
}

:root.dark {
  --bg-body: #111827;
  --bg-card: #1f2937;
  --text-primary: #f3f4f6;
  --text-regular: #e5e7eb;
  --text-secondary: #9ca3af;
  --border-color: #374151;
  --header-bg: #1f2937;
  --aside-bg: #1f2937;
  
  /* Element UI Colors Dark */
  --el-table-bg: #1f2937;
  --el-table-tr-bg: #1f2937;
  --el-table-stripe-bg: rgba(255, 255, 255, 0.02);
  --el-table-hover-bg: rgba(255, 255, 255, 0.05);
  --el-table-border-color: #374151;
  --el-table-header-color: #f3f4f6;
  --el-table-text-color: #e5e7eb;
  
  --el-input-bg: #111827;
  --el-input-border: #374151;
  --el-input-text: #f3f4f6;
  --el-input-disabled-bg: rgba(255, 255, 255, 0.05);
  
  --el-menu-hover-bg: rgba(255, 255, 255, 0.05);
  --el-menu-active-bg: rgba(64, 158, 255, 0.1);

  --table-header-bg: #2d3748;
}

#app {
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "PingFang SC", "Hiragino Sans GB",
    "Microsoft YaHei", "Noto Sans CJK SC", "Helvetica Neue", Arial, sans-serif;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  color: var(--text-primary);
  background-color: var(--bg-body);
  min-height: 100vh;
  transition: background-color 0.3s, color 0.3s;
}

/* Global Transition */
*, *::before, *::after {
  transition: background-color 0.3s, border-color 0.3s, color 0.3s;
}

html,
body {
  margin: 0;
  padding: 0;
  background: var(--bg-body);
  color: var(--text-primary);
}

/* Element UI Overrides (Theme Agnostic via Variables) */
.el-table,
.el-table__expanded-cell {
  background-color: var(--el-table-bg) !important;
  color: var(--el-table-text-color) !important;
}
.el-table th,
.el-table tr {
  background-color: var(--el-table-tr-bg) !important;
}
.el-table th {
  color: var(--el-table-header-color) !important;
}
.el-table--striped .el-table__body tr.el-table__row--striped td {
  background-color: var(--el-table-stripe-bg) !important;
}
.el-table td,
.el-table th.is-leaf {
  background-color: var(--el-table-bg) !important;
  border-bottom-color: var(--el-table-border-color) !important;
  border-right-color: var(--el-table-border-color) !important;
}
.el-table--border,
.el-table--group {
  border-color: var(--el-table-border-color) !important;
}
.el-table--border th,
.el-table--border td {
  border-right-color: var(--el-table-border-color) !important;
}
.el-table--enable-row-hover .el-table__body tr:hover > td {
  background-color: var(--el-table-hover-bg) !important;
}
.el-table__body tr.current-row > td {
  background-color: var(--el-menu-active-bg) !important;
}
.el-table::before {
  background-color: var(--el-table-border-color) !important;
}
.el-table__fixed-right::before,
.el-table__fixed::before {
  background-color: var(--el-table-border-color) !important;
}

/* Input & Textarea */
.el-input__inner,
.el-textarea__inner {
  background-color: var(--el-input-bg) !important;
  border-color: var(--el-input-border) !important;
  color: var(--el-input-text) !important;
}
.el-input__inner:hover,
.el-textarea__inner:hover {
  border-color: #c0c4cc; /* Element Default Hover */
}
.dark .el-input__inner:hover,
.dark .el-textarea__inner:hover {
  border-color: #666;
}
.el-input.is-disabled .el-input__inner,
.el-textarea.is-disabled .el-textarea__inner {
  background-color: var(--el-input-disabled-bg) !important;
  border-color: var(--el-input-border) !important;
  color: var(--text-secondary) !important;
  cursor: not-allowed;
}

/* Menu */
.el-menu {
  background-color: var(--aside-bg) !important;
  border-right-color: var(--border-color) !important;
}
.el-menu-item {
  color: var(--text-regular) !important;
}
.el-menu-item:focus,
.el-menu-item:hover {
  background-color: var(--el-menu-hover-bg) !important;
}
.el-menu-item.is-active {
  color: #409eff !important;
  background-color: var(--el-menu-active-bg) !important;
}

/* Common Components */
.el-form-item__label {
  color: var(--text-regular) !important;
}

.el-card {
  background-color: var(--bg-card) !important;
  border-color: var(--border-color) !important;
  color: var(--text-primary) !important;
}
.el-dialog {
  background-color: var(--bg-card) !important;
}
.el-dialog__title {
  color: var(--text-primary) !important;
}
.el-dialog__body {
  color: var(--text-regular) !important;
}

/* Dropdowns */
.el-select-dropdown,
.el-picker-panel,
.el-cascader-panel {
  background-color: var(--bg-card) !important;
  border-color: var(--border-color) !important;
}
.el-select-dropdown__item {
  color: var(--text-regular) !important;
}
.el-select-dropdown__item.hover,
.el-select-dropdown__item:hover {
  background-color: var(--el-menu-hover-bg) !important;
}
.el-select-dropdown__item.selected {
  color: #409eff !important;
  background-color: var(--el-menu-active-bg) !important;
}
.el-picker-panel__icon-btn {
  color: var(--text-primary) !important;
}
.el-date-table th {
  border-bottom-color: var(--border-color) !important;
  color: var(--text-primary) !important;
}
.el-date-picker__header-label {
  color: var(--text-primary) !important;
}

/* Pagination */
.el-pagination button:disabled,
.el-pagination .btn-prev,
.el-pagination .btn-next,
.el-pager li {
  background-color: var(--bg-card) !important;
  color: var(--text-regular) !important;
}
.el-pagination__total,
.el-pagination__jump {
  color: var(--text-regular) !important;
}

/* Tabs */
.el-tabs__item {
  color: var(--text-regular) !important;
}
.el-tabs__item.is-active,
.el-tabs__item:hover {
  color: #409eff !important;
}
.el-tabs__nav-wrap::after {
  background-color: var(--border-color) !important;
}

/* Dark Mode Specific Overrides (Only for things that can't be handled by vars easily) */
.dark .el-loading-mask {
  background-color: rgba(0, 0, 0, 0.8);
}
.dark .el-message-box {
  background-color: var(--bg-card);
  border-color: var(--border-color);
}
.dark .el-message-box__title {
  color: var(--text-primary);
}
.dark .el-message-box__content {
  color: var(--text-regular);
}
.dark .el-drawer {
  background-color: var(--bg-card);
}
.dark .el-drawer__header {
  color: var(--text-primary);
}
.dark .el-button--default {
  background-color: transparent;
  border-color: var(--border-color);
  color: var(--text-regular);
}
.dark .el-button--default:hover,
.dark .el-button--default:focus {
  color: #409eff;
  border-color: #c6e2ff;
  background-color: rgba(64, 158, 255, 0.1);
}
.dark .el-switch__label {
  color: var(--text-secondary);
}
.dark .el-switch__label.is-active {
  color: #409eff;
}

/* Scrollbar adaptation */
.dark ::-webkit-scrollbar {
  width: 10px;
  height: 10px;
}
.dark ::-webkit-scrollbar-track {
  background: var(--bg-body);
}
.dark ::-webkit-scrollbar-thumb {
  background: #4b5563;
  border-radius: 5px;
}
.dark ::-webkit-scrollbar-thumb:hover {
  background: #6b7280;
}

.dark .el-progress-bar__outer {
  background-color: rgba(255, 255, 255, 0.1);
}
.dark .el-steps--simple {
  background-color: var(--bg-body);
}
.dark .el-step__title {
  color: var(--text-secondary);
}
.dark .el-step__title.is-process {
  color: var(--text-primary);
}
.dark .el-step__title.is-finish {
  color: #409eff;
}
</style>
